import { BlogCard } from '@/components/BlogCard';
import { BlogHeader } from '@/components/BlogHeader';
import { blogPosts } from '@/data/blogPosts';
import { useSEO } from '@/hooks/useSEO';
import { useTranslation } from '@/hooks/useTranslation';
import { Compass, BookOpen } from 'lucide-react';
import { useEffect } from 'react';

const Blog = () => {
  const { language } = useTranslation();
  const publishedPosts = blogPosts.filter(post => post.published);

  useSEO({
    title: 'Blog - Travel & Culture Insights | DiskOver',
    description: 'Discover travel tips, cultural insights, and app comparisons. Learn how to explore cities authentically with our expert guides and reviews.',
    keywords: 'travel blog, culture blog, travel apps, city exploration, cultural tourism, travel guides',
    ogTitle: 'DiskOver Blog - Travel & Culture Insights',
    ogDescription: 'Expert guides and reviews for cultural travel and city exploration',
    canonical: 'https://diskover.ovh/blog'
  });

  useEffect(() => {
    // JSON-LD for Blog page
    const blogSchema = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'DiskOver Blog',
      description: 'Travel and cultural exploration insights',
      url: 'https://diskover.ovh/blog',
      publisher: {
        '@type': 'Organization',
        name: 'DiskOver',
        logo: {
          '@type': 'ImageObject',
          url: 'https://diskover.ovh/logo.png'
        }
      },
      blogPost: publishedPosts.map(post => ({
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        url: `https://diskover.ovh/blog/${post.slug}`,
        image: `https://diskover.ovh${post.featuredImage}`,
        author: {
          '@type': 'Organization',
          name: post.author
        }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(blogSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [publishedPosts]);

  return (
    <div className="min-h-screen bg-background">
      <BlogHeader />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Compass className="w-8 h-8 text-primary animate-pulse" />
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight">
              Travel & Culture{' '}
              <span className="text-primary">Insights</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover expert guides, app comparisons, and cultural stories to help you explore cities authentically.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          {publishedPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground">No articles published yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {publishedPosts.map((post) => (
                <BlogCard key={post.slug} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
